// GtSelect + GtDatePicker — house-style form controls (teal), replacing native
// <select> and <input type=date> whose OS popups cannot be styled.
import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

// A popover positioned inside its own wrapper is clipped by any ancestor that
// scrolls or hides overflow, which is every card in this app. Measuring the
// trigger and positioning against the viewport is the only reliable fix. Flips
// above the trigger when there is more room there.
function usePopoverPos(open, anchorRef) {
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    if (!open) { setPos(null); return undefined; }
    const place = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const below = window.innerHeight - r.bottom;
      const flip = below < 240 && r.top > below;
      setPos({
        position: 'fixed',
        left: Math.round(r.left),
        minWidth: Math.round(r.width),
        maxHeight: Math.max(160, Math.round((flip ? r.top : below) - 16)),
        ...(flip
          ? { top: 'auto', bottom: Math.round(window.innerHeight - r.top + 4) }
          : { top: Math.round(r.bottom + 4), bottom: 'auto' }),
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, anchorRef]);
  return pos;
}

export function GtSelect({ value, options, onChange, placeholder = '—', width }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const btnRef = useRef(null);
  const pos = usePopoverPos(open, btnRef);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const opts = options.map(o => (typeof o === 'object' ? o : { value: o, label: o === '' ? placeholder : String(o).replace(/_/g, ' ') }));
  const current = opts.find(o => String(o.value) === String(value ?? ''));
  return (
    <div className="gts-wrap" ref={ref} style={width ? { width } : undefined}>
      <button ref={btnRef} type="button" className={'gts-btn' + (open ? ' gts-btn--open' : '')} onClick={() => setOpen(o => !o)}>
        <span className={current && current.value !== '' ? 'gts-val' : 'gts-ph'}>{current ? current.label : placeholder}</span>
        <span className="gts-chev">▾</span>
      </button>
      {open && (
        <div className="gts-pop" style={pos || undefined}>
          {opts.map(o => {
            const on = String(o.value) === String(value ?? '');
            return (
              <div key={String(o.value)} className={'gts-item' + (on ? ' gts-item--on' : '')}
                onClick={() => { onChange(o.value); setOpen(false); }}>
                <span>{o.label}</span>
                {on && <span className="gts-check">✓</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// C1-SEARCHSELECT: GtSelect with a search box, for long lists (hundreds or
// thousands of options). Shows the first `limit` matches; typing narrows across
// the whole list. Enter picks the top match, Escape closes.
// C1-SEARCHSELECT2: the popup is portalled to <body> so no card, modal or
// preview can clip or paint over it, and it has a real width.
export function GtSearchSelect({ value, options, onChange, placeholder = 'Choose', searchPlaceholder = 'Type to search', width, limit = 50 }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const inputRef = useRef(null);
  const pos = usePopoverPos(open, btnRef);
  useEffect(() => {
    if (!open) { setQ(''); return undefined; }
    const close = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', close);
    const t = setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 0);
    return () => { document.removeEventListener('mousedown', close); clearTimeout(t); };
  }, [open]);
  const current = options.find((o) => String(o.value) === String(value ?? ''));
  const needle = q.trim().toLowerCase();
  const matches = needle ? options.filter((o) => String(o.label).toLowerCase().indexOf(needle) !== -1) : options;
  const shown = matches.slice(0, limit);
  const pick = (v) => { onChange(v); setOpen(false); };
  const popWidth = pos ? Math.max(200, Math.min(Math.max(pos.minWidth, 380), window.innerWidth - pos.left - 16)) : undefined;
  return (
    <div className="gts-wrap" ref={ref} style={width ? { width } : undefined}>
      <button ref={btnRef} type="button" className={'gts-btn' + (open ? ' gts-btn--open' : '')} onClick={() => setOpen((o) => !o)}>
        <span className={current ? 'gts-val' : 'gts-ph'}>{current ? current.label : placeholder}</span>
        <span className="gts-chev">{'\u25be'}</span>
      </button>
      {open && pos && createPortal(
        <div ref={popRef} className="gts-pop gtss-pop" style={{ ...pos, width: popWidth }} onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
          <input ref={inputRef} className="gtss-input" value={q} placeholder={searchPlaceholder} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); }
              if (e.key === 'Enter' && shown.length) { e.preventDefault(); pick(shown[0].value); }
            }} />
          {shown.length === 0 && <div className="gtss-note">No one matches.</div>}
          {shown.map((o) => {
            const on = String(o.value) === String(value ?? '');
            return (
              <div key={String(o.value)} className={'gts-item' + (on ? ' gts-item--on' : '')} onClick={() => pick(o.value)}>
                <span className="gtss-label">{o.label}</span>
                {on && <span className="gts-check">{'\u2713'}</span>}
              </div>
            );
          })}
          {matches.length > shown.length && <div className="gtss-note">Showing {shown.length} of {matches.length}. Keep typing to narrow it down.</div>}
        </div>,
        document.body
      )}
    </div>
  );
}

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DOW = ['M','T','W','T','F','S','S'];

export function GtDatePicker({ value, onChange, placeholder = 'Pick a date', width }) {
  const [open, setOpen] = useState(false);
  const sel = value ? new Date(value) : null;
  const [view, setView] = useState(() => { const d = sel && !isNaN(sel) ? sel : new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  // days -> months -> years. Paging a birth year one month at a time is not a
  // date picker, it is a punishment.
  const [mode, setMode] = useState('days');
  const ref = useRef(null);
  const btnRef = useRef(null);
  const pos = usePopoverPos(open, btnRef);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const first = new Date(view.y, view.m, 1);
  const startDow = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const today = new Date();
  const isSame = (d, ref2) => ref2 && d === ref2.getDate() && view.m === ref2.getMonth() && view.y === ref2.getFullYear();
  const fmt = sel && !isNaN(sel) ? sel.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : null;
  const pick = (d) => { const iso = `${view.y}-${String(view.m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`; onChange(iso); setOpen(false); };
  const navMonth = (delta) => setView(v => { const m = v.m + delta; return { y: v.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 }; });
  const yearPage = Math.floor(view.y / 12) * 12;
  const nav = (delta) => {
    if (mode === 'days') return navMonth(delta);
    if (mode === 'months') return setView(v => ({ ...v, y: v.y + delta }));
    return setView(v => ({ ...v, y: v.y + delta * 12 }));
  };
  const headLabel = mode === 'days' ? MONTHS[view.m] + ' ' + view.y
    : mode === 'months' ? String(view.y)
    : yearPage + ' \u2013 ' + (yearPage + 11);
  const selYear = sel && !isNaN(sel) ? sel.getFullYear() : null;
  const selMonth = sel && !isNaN(sel) ? sel.getMonth() : null;

  return (
    <div className="gts-wrap" ref={ref} style={width ? { width } : undefined}>
      <button ref={btnRef} type="button" className={'gts-btn' + (open ? ' gts-btn--open' : '')}
        onClick={() => { setMode('days'); setOpen(o => !o); }}>
        <span className={fmt ? 'gts-val' : 'gts-ph'}>{fmt || placeholder}</span>
        <span className="gts-chev">▾</span>
      </button>
      {open && (
        <div className="gts-pop gtd-pop" style={pos || undefined}>
          <div className="gtd-head">
            <button type="button" className="gtd-nav" onClick={() => nav(-1)}>‹</button>
            <button type="button" className="gtd-title gtd-titlebtn" title="Switch between day, month and year"
              onClick={() => setMode(m => (m === 'days' ? 'months' : m === 'months' ? 'years' : 'days'))}>
              {headLabel}
            </button>
            <button type="button" className="gtd-nav" onClick={() => nav(1)}>›</button>
          </div>
          {mode === 'years' && (
            <div className="gtd-mgrid">
              {Array.from({ length: 12 }, (_, i) => yearPage + i).map(y => (
                <button type="button" key={y}
                  className={'gtd-cell' + (selYear === y ? ' gtd-cell--sel' : '')}
                  onClick={() => { setView(v => ({ ...v, y })); setMode('months'); }}>{y}</button>
              ))}
            </div>
          )}
          {mode === 'months' && (
            <div className="gtd-mgrid">
              {MONTHS.map((mn, i) => (
                <button type="button" key={mn}
                  className={'gtd-cell' + (selYear === view.y && selMonth === i ? ' gtd-cell--sel' : '')}
                  onClick={() => { setView(v => ({ ...v, m: i })); setMode('days'); }}>{mn.slice(0, 3)}</button>
              ))}
            </div>
          )}
          {mode === 'days' && (
          <div className="gtd-grid">
            {DOW.map((d, i) => <span key={'h' + i} className="gtd-dow">{d}</span>)}
            {cells.map((d, i) => d === null
              ? <span key={'e' + i} />
              : <button type="button" key={'d' + i}
                  className={'gtd-day' + (isSame(d, sel) ? ' gtd-day--sel' : '') + (isSame(d, today) ? ' gtd-day--today' : '')}
                  onClick={() => pick(d)}>{d}</button>)}
          </div>
          )}
          <div className="gtd-foot">
            <button type="button" className="gtd-link" onClick={() => { onChange(null); setOpen(false); }}>Clear</button>
            <button type="button" className="gtd-link" onClick={() => { const t = new Date(); setView({ y: t.getFullYear(), m: t.getMonth() }); pick(t.getDate()); }}>Today</button>
          </div>
        </div>
      )}
    </div>
  );
}
